/**
 * URE-2 / UPE-3 — checklist-requirement → SED-section map.
 *
 * The Coverage Matrix asks "is requirement X covered?". The SED slot
 * model asks "is section Y populated?". The two are not 1-to-1: a
 * requirement (e.g. `'utility-letter'`) typically fills several
 * sub-fields on one section (`grid.utilityName`, `grid.capacity`,
 * `grid.voltage`, `grid.interconnection`); a section can also be fed
 * by multiple requirements (a parcel survey + a zoning letter both
 * write to `parcel`).
 *
 * This module is the bridge:
 *
 *  - **URE-2** — when `extractFromDocument` runs on a doc with
 *    `intendedRequirementIds`, it consults this map to prioritise the
 *    matching SED sections FIRST, so a 429 / timeout mid-run still
 *    yields the most relevant fields.
 *
 *  - **UPE-3** — `computeRequirementStatus` walks this map's section
 *    list to ask "did any of the slots that fulfill requirement X
 *    actually get populated?" — Tier 2 (slot-aware) coverage, replacing
 *    the cliff-1 doc-match-score-only heuristic.
 *
 * Keys are `ChecklistItem.id` values from
 * `src/config/documentChecklists.ts`; values are `SedSectionId`s from
 * `src/types/siteEvidence.ts`. The map is exhaustive across every
 * requirement the platform currently surfaces in the matrix.
 *
 * NOTE on granularity: the SED's `EvidenceSection<T>` is the smallest
 * status-bearing unit — fields inside a section don't carry their own
 * `status`. So this map points at the *parent section*, not the field
 * path. Tier-2 coverage is "section is not unavailable AND was linked
 * to a doc"; field-level coverage is handled by `DOC_OVERRIDE_RULES`
 * (URE-3 expands that table in lock-step).
 */
import type { SedSectionId } from '../types/siteEvidence';

export const REQUIREMENT_TO_SECTION_MAP: Record<string, ReadonlyArray<SedSectionId>> = {
  // Feasibility
  // SED D2 (H-6) — hazard requirements now also cover the typed `multiHazard`
  // section (FEMA NRI wildfire / strong-wind / hail / drought + composite,
  // beyond the existing seismic + flood sections).
  'geotech': ['geotechnical', 'seismic', 'parcel', 'multiHazard'],
  'utility-letter': ['grid', 'electricity', 'onSiteInfrastructure'],
  'parcel-survey': ['parcel', 'siteProfile'],
  'alta-survey': ['survey', 'parcel', 'topography'],
  'mineral-title': ['mineralResources'],
  'zoning': ['parcel', 'regulatory'],
  'flood-cert': ['flood', 'multiHazard'],

  // Environmental
  'phase1-esa': ['environmental', 'siteResearch'],
  'wetland': ['environmental'],
  'species-survey': ['environmental'],
  'cultural': ['culturalResources'],
  'archaeological-survey': ['culturalResources'],

  // Power availability
  // SED D2 — the iso-queue requirement now also covers the typed
  // `interconnection` section (ISO/RTO queue position + declared status).
  // SED D2 (H-6) — + the typed `transmissionHeadroom` section (ATC / hosting
  // capacity / nodal LMP — honest-unavailable until a real fetcher lands).
  'iso-queue': ['grid', 'interconnection', 'transmissionHeadroom'],
  'tariff': ['electricity'],

  // Water resource
  // SED D2 (H-6) — water requirements now also cover the typed `waterResource`
  // section (consumptive-use AFY / water-rights / aquifer depth / NPDES —
  // site-specific, distinct from the basin-level `waterStress` section).
  'water-rights': ['waterStress', 'waterResource'],
  'aquifer-study': ['waterStress', 'siteResearch'],
  'discharge-permit': ['environmental', 'waterStress', 'waterResource'],
  // P6 — municipal utility statement; `waterResource` first so the channel-B
  // primary-section sync (DOCUMENT_FED_SECTIONS) points at the right slot.
  'municipal-utility-letter': ['waterResource', 'waterStress', 'environmental'],

  // Test-fit
  'survey': ['survey', 'topography', 'siteProfile', 'parcel'],
  'utility-locate': ['parcel', 'siteProfile'],

  // Interconnection (fiber)
  'fiber-survey': ['connectivity'],
  'latency-test': ['connectivity'],

  // Power availability — BTM
  'btm-vendor-quote': [
    'solar',
    'wind',
    'electricity',
    'grid',
    'climate',
    'waterStress',
    'siteProfile',
    'onSiteInfrastructure',
  ],

  // Supply chain
  'vendor-quote-pack': ['siteResearch', 'grid'],

  // Public sentiment / synthesis / custom brief
  'community-feedback-summary': ['publicSentiment', 'siteResearch'],
  'diligence-source-pack': ['siteResearch', 'grid', 'regulatory'],
  'prior-report-pack': ['siteResearch'],

  // Report setup + Site Project Intent (Coverage Matrix)
  'site-project-intent': [
    'siteProfile',
    'grid',
    'electricity',
    'climate',
    'waterStress',
    'onSiteInfrastructure',
  ],
  // SED D2 (H-6) — feasibility setup covers the typed `multiHazard` section.
  'feasibility-report-setup': ['siteProfile', 'grid', 'multiHazard'],
  'supply-chain-risk-report-setup': ['siteProfile', 'grid'],
  'environmental-report-setup': ['environmental', 'regulatory'],
  'test-fit-report-setup': ['siteProfile', 'parcel'],
  // SED D2 — power-availability + interconnection report setups now cover the
  // typed `interconnection` section; financial setup covers `incentives`.
  // SED D2 (H-6) — power-availability setup also covers `transmissionHeadroom`;
  // water-resource setup also covers the typed `waterResource` section.
  'power-availability-report-setup': ['grid', 'electricity', 'interconnection', 'transmissionHeadroom'],
  // P3.8 follow-through — the evidence-probed Public Sentiment scan row.
  'public-sentiment-report-setup': ['publicSentiment'],
  'water-resource-report-setup': ['waterStress', 'environmental', 'waterResource'],
  'interconnection-report-setup': ['connectivity', 'interconnection'],
  'custom-brief-report-setup': ['siteResearch'],
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report setup. Cost-basis SED.
  'financial-report-setup': ['grid', 'electricity', 'incentives'],
  'executive-summary-report-setup': ['siteResearch'],
};

/**
 * Reverse lookup helper — given a SED section id, which checklist
 * requirements (if any) include this section in their cover map.
 * Used by `computeRequirementStatus`'s Tier 2 (UPE-3) when checking
 * authored fulfilment.
 */
export function requirementsForSection(sectionId: SedSectionId): string[] {
  const out: string[] = [];
  for (const [reqId, sections] of Object.entries(REQUIREMENT_TO_SECTION_MAP)) {
    if (sections.includes(sectionId)) out.push(reqId);
  }
  return out;
}
